import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { CompanyConfigService } from '../services';
import {
  clearQRScanDataAction,
  getCompanyConfigAction,
  getCompanyConfigErrorAction,
  getCompanyConfigSuccessAction,
  setCompanyConfigIndexAction,
} from './company-config.actions';
import { CompanyConfigFacade } from './company-config.facade';
import { CompanyConfigType } from '../enum';
import { Storage } from '@ionic/storage-angular';
// import { LocalStorageService } from '@symphony-solo/shared/local-storage/src';
import { CartFacade } from '@symphony-solo/domain/cart/src/lib/+state/cart.facade';

@Injectable()
export class CompanyConfigEffects {
  constructor(
    private action$: Actions,
    private companyConfigService: CompanyConfigService,
    private companyConfigFacade: CompanyConfigFacade,
    private cartFacade: CartFacade,
    private storage: Storage
  ) {}

  protected getCompanyConfig$ = createEffect(() =>
    this.action$.pipe(
      ofType(getCompanyConfigAction),
      switchMap(() => {
        return this.companyConfigService.getCompanyConfig().pipe(
          map((companyConfig: any) => {
            return getCompanyConfigSuccessAction({ companyConfig });
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on getting company config', ApiResponseCode.Server_Error))
              .build();
            return of(getCompanyConfigErrorAction({ failure }));
          })
        );
      })
    )
  );

  protected companyConfigSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(getCompanyConfigSuccessAction),
      switchMap(({ companyConfig }) => {
        const DEFAULT_COLOR_FOR_TEXT = '#000000';
        const companyConfigIndex = [];
        const excludedBooleanTypes = [CompanyConfigType.SoloClickCollectLocationTemplateType];
        for (const config of companyConfig) {
          if (config?.id === CompanyConfigType.AnonymousBrowsing && config?.value === '1') {
            this.companyConfigFacade.companyConfigSubj.next(true);
            this.companyConfigFacade.anonymousBrowsing.next(true);
            // this.companyConfigFacade.setIsAnonymous(true);
          }

          if (
            config?.id === CompanyConfigType.SoloAltColourForTextIconOnBackground &&
            config?.value !== null &&
            config?.value !== DEFAULT_COLOR_FOR_TEXT
          ) {
            this.companyConfigFacade.setIsContrastColor(true);
          }

          if (config.value === '0' && !excludedBooleanTypes.includes(config?.id)) {
            companyConfigIndex[config.id] = false;
          } else if (config.value === '1' && !excludedBooleanTypes.includes(config?.id)) {
            companyConfigIndex[config.id] = true;
          } else {
            companyConfigIndex[config.id] = config.value;
          }
        }

        return of(setCompanyConfigIndexAction({ companyConfigIndex }));
      })
    )
  );

  protected clearQRScanData$ = createEffect(() =>
    this.action$.pipe(
      ofType(clearQRScanDataAction),
      withLatestFrom(this.companyConfigFacade.isOrderFromSite$, this.cartFacade.cart$),
      switchMap(([action, isOrderFromSite, cart]) => {
          this.storage.remove('CART_DATE_TIME');
          this.storage.remove('CART');
          this.storage.remove('ORDER_FROM_SITE');
          this.storage.remove('SID');
          this.storage.get('ALLERGENS_AGREED').then((ALLERGENS_AGREED) => {
            if (ALLERGENS_AGREED) {
              this.storage.remove('ALLERGENS_AGREED');
              if (cart === null) {
                this.cartFacade.loadCart([]);
              }
            }
          });
        return EMPTY;
      })
    )
  );

  // protected getCompanyConfigError$ = createEffect(() =>
  //   this.action$.pipe(
  //     ofType(getCompanyConfigErrorAction)
  //   )
  // );
}
